// theaters.ts
// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
//
// COP — the GENERIC multi-INT theater loader (the additional named theaters:
// Taiwan Strait + Levant). Mirrors ../scenarios/hormuz.ts's envelope projection so
// every theater publishes onto the SAME bus → LOCAL PDP (I4) → render path, and the
// clearance filter bites on the WHOLE picture (permit → show / deny → absent).
//
// WHY A SEPARATE GENERIC LOADER: the Hormuz loader (hormuz.ts) is the load-bearing,
// motion-rich reference theater (waypoint/orbit motion models, the demonstrator's
// default). The Taiwan/Levant corpus datasets share Hormuz's record shape but are
// STATIC postures (no `motion` block) — so they re-use the same record→envelope
// projection + Living-Net resting-read tagging here, parameterized by the dataset +
// the theater id/policy_ref, WITHOUT touching hormuz.ts. taiwan.ts / levant.ts are
// thin wrappers that bind their JSON to startTheaterScenario.
//
// SOURCE OF TRUTH: ./scenario-taiwan.json / ./scenario-levant.json (the corpus track
// shape). We only PROJECT them into canonical envelopes here; no contract/schema is
// mutated — confidence/heading/speed ride in the open `properties` bag (P1).

import type {
  CanonicalEnvelope,
  Affiliation,
  EnvelopeKind,
  PayloadMode,
  LeakageProfile,
} from "../../../../packages/schema/ts/envelope.ts";
import type { EnvelopeBus } from "../host/envelopeBus.ts";
import { altitudeHaeM } from "./altitude.ts";
import { straightLinePositionAtMs, type MotionRecord } from "../../../../packages/motion-model/src/motion.ts";
import {
  type TrustTier,
  trustTierOfRecord,
  isPresenceRecord,
  decayedConfidence,
} from "../host/livingNet.ts";

/** The raw record shape in a theater JSON (same fields as a HormuzRecord; the
 *  additional theaters carry `protected` and no `motion`/`orbit`). */
export interface TheaterRecord {
  id: string;
  kind: string;
  domain: string;
  affiliation: Affiliation;
  type: string;
  name: string;
  callsign?: string;
  lat: number;
  lon: number;
  headingDeg: number;
  speedKn: number;
  classification: string;
  releasability: string[];
  mission?: string[];
  caveats?: string[];
  /** Authored motion block (theater JSON is cast raw). Only altitude_ft is read on
   *  projection — airborne cruise alt → inspector FL###; the static-theater path
   *  doesn't model the rest of the motion model. */
  motion?: { altitude_ft?: number };
  protected?: boolean;
}

/** Per-record live state advanced (straight-line) on each tick. */
interface LiveTrack {
  rec: TheaterRecord;
  lat: number;
  lon: number;
  headingDeg: number;
  speedKn: number;
  confidence: number;
  staleMs: number;
  trustTier: TrustTier;
  presence: boolean;
}

/** A named theater: the dataset + the metadata the projection stamps onto every
 *  envelope (so personae/source filters can tell theaters apart). */
export interface TheaterDef {
  /** Stable theater id (e.g. "taiwan", "levant") — used in the source tag + policy_ref. */
  id: string;
  /** Human label for logs / the (future) scenario switch UI. */
  label: string;
  /** The corpus records (the imported JSON). */
  records: TheaterRecord[];
}

// --- Render-kind mapping (identical to hormuz.ts) ----------------------------
const RENDER_KIND: Record<string, EnvelopeKind> = {
  track: "track",
  alert: "alert",
  observation: "observation",
  asset: "observation",
};
function renderKind(k: string): EnvelopeKind {
  return RENDER_KIND[k] ?? "observation";
}

// --- Protection treatment (identical rule to hormuz.ts) ----------------------
const SCI_SAP_PREFIX = /^(sci|compartment|sap|accm)\s*:/i;
function isProtected(rec: TheaterRecord): boolean {
  if (rec.protected) return true;
  if (rec.classification === "TOP_SECRET") return true;
  return (rec.mission ?? []).some((m) => SCI_SAP_PREFIX.test(m));
}

// --- Deterministic synthetic-but-coherent fields (identical to hormuz.ts) ----
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

function birthConfidence(rec: TheaterRecord): number {
  const base =
    rec.affiliation === "friend"
      ? 0.9
      : rec.affiliation === "hostile"
        ? 0.78
        : rec.affiliation === "neutral"
          ? 0.7
          : rec.affiliation === "unknown"
            ? 0.5
            : 0.4;
  const jitter = (hash01(rec.id) - 0.5) * 0.2;
  return Math.max(0.2, Math.min(0.99, +(base + jitter).toFixed(2)));
}

function confirmedFraction(rec: TheaterRecord): number {
  return +hash01(rec.id + "confirm").toFixed(3);
}

function confidenceFor(rec: TheaterRecord, tier: TrustTier): number {
  const birth = birthConfidence(rec);
  return tier === "crowd"
    ? decayedConfidence("crowd", birth, confirmedFraction(rec))
    : birth;
}

function staleWindowMs(rec: TheaterRecord, presence: boolean): number {
  if (presence) return 30_000 + Math.round(hash01(rec.id + "beat") * 45_000);
  return 90_000 + Math.round(hash01(rec.id + "stale") * 150_000);
}

// --- Motion (straight-line; these theaters fly straight, ignoring waypoint/orbit) ---
// Motion is now a PURE wall-clock function of (record, absMs) — see ./motion.ts. These
// theaters straight-line displace from their seed at speedKn/headingDeg (the legacy
// behaviour), but epoch-anchored + period-wrapped so every client computes the SAME
// position for the SAME track at the SAME instant (deterministic shared picture).

// --- Forward-extrapolated motion path (SHARED CROSS-LANE CONTRACT) -----------
// properties.future_path: up to 8 forward positions [lon, lat, altMeters] at +30s..
// +240s from the deterministic motion model (theaters fly straight-line, so this uses
// straightLinePositionAtMs forward-eval). ABSENT for a static (zero-speed) record so the
// render lane (L-RENDER) draws nothing. Additive to properties — NO schema/PDP change.
const FUTURE_PATH_STEPS = 8;
const FUTURE_PATH_STEP_MS = 30_000;

/** The deterministic forward path for a theater record: straightLinePositionAtMs at
 *  +30s..+240s, each [lon, lat, altMeters] (altitude model HAE metres, 0 when surface).
 *  Returns undefined for a static record (so future_path is ABSENT). Epoch-anchored ⇒
 *  coherent across clients. */
function futurePathFor(
  rec: TheaterRecord,
  altM: number | null,
): Array<[number, number, number]> | undefined {
  if (!(rec.speedKn > 0)) return undefined; // static posture — no forward trajectory
  const now = Date.now();
  const alt = altM ?? 0;
  const path: Array<[number, number, number]> = [];
  for (let n = 1; n <= FUTURE_PATH_STEPS; n++) {
    const p = straightLinePositionAtMs(rec as MotionRecord, now + n * FUTURE_PATH_STEP_MS);
    path.push([p.lon, p.lat, alt]);
  }
  return path;
}

// --- Envelope projection (mirrors hormuz.ts toEnvelope, theater-parameterized) -
const iso = (offsetMs = 0): string => new Date(Date.now() + offsetMs).toISOString();

function toEnvelope(t: LiveTrack, theaterId: string): CanonicalEnvelope {
  const rec = t.rec;
  const protectedObj = isProtected(rec);
  const leakage: LeakageProfile = protectedObj ? "clear-shell" : "clear";
  const payloadMode: PayloadMode = protectedObj ? "encrypted" : "clear";
  const now = iso();
  // 2.5D-DEPTH: air/UAS tracks get a real HAE altitude (metres) from the pure altitude model.
  const altM = altitudeHaeM(rec.domain, rec.type, rec.id);
  // SHARED CONTRACT: forward motion path for the render lane's smear/trail (absent if static).
  const futurePath = futurePathFor(rec, altM);
  return {
    id: rec.id,
    schema: "scop.envelope/1.0",
    kind: renderKind(rec.kind),
    time: now,
    valid_time: { start: iso(-1_000), stale: iso(t.staleMs) },
    // hormuz/theater JSON is flat {lat, lon}; GeoJSON geometry is [lon, lat].
    // 2.5D-DEPTH: air/UAS carry a 3rd HAE coordinate (metres) so the tilted map lifts them.
    geometry: {
      type: "Point",
      coordinates: altM != null ? [t.lon, t.lat, altM] : [t.lon, t.lat],
    },
    properties: {
      name: rec.name,
      ...(rec.callsign ? { callsign: rec.callsign } : {}),
      affiliation: rec.affiliation,
      type: rec.type,
      domain: rec.domain,
      heading_deg: Math.round(t.headingDeg),
      speed_kn: Math.round(t.speedKn),
      // 2.5D-DEPTH: altitude_m (metres HAE) — canonical Z store the renderer reads for the Z-lift.
      ...(altM != null ? { altitude_m: altM } : {}),
      // Airborne cruise altitude (ft), authored in the motion block → inspector FL###.
      ...(rec.motion?.altitude_ft != null ? { altitude_ft: rec.motion.altitude_ft } : {}),
      // SHARED CONTRACT: future_path — up to 8 forward [lon,lat,altM] positions the render
      // lane reads to draw the predictive smear/trail. Absent when the record is static.
      ...(futurePath ? { future_path: futurePath } : {}),
      confidence: t.confidence,
      trust_tier: t.trustTier,
      crowd_pip: t.trustTier === "crowd",
      presence: t.presence,
      ...(t.presence ? { presence_seen: now } : {}),
      orig_kind: rec.kind,
      source: `${theaterId}/multi-int`,
    },
    security: {
      classification: rec.classification,
      releasability: rec.releasability,
      mission: rec.mission ?? [],
      caveats: rec.caveats ?? [],
      originator_control: (rec.caveats ?? []).includes("ORCON"),
      policy_ref: `policy://${theaterId}/multi-int`,
      payload_mode: payloadMode,
      leakage_profile: leakage,
    },
    provenance: {
      originator: `${theaterId}-multi-int`,
      adapter: `${theaterId}/${rec.domain}`,
      received_at: now,
      chain: [{ adapter: `${theaterId}/${rec.domain}`, at: now }],
    },
    payload: protectedObj
      ? { encrypted_ref: `scop:tdf:${rec.id}` }
      : { clear: { domain: rec.domain } },
  } as CanonicalEnvelope;
}

// --- Public API --------------------------------------------------------------

export interface TheaterHandle {
  stop(): void;
}

/** Build the live track set for a theater (initial positions + per-record
 *  confidence/staleness/Living-Net tier). Exposed for tests / inspection. */
export function buildTheaterTracks(def: TheaterDef): LiveTrack[] {
  return def.records.map((rec) => {
    const trustTier = trustTierOfRecord(rec);
    const presence = isPresenceRecord(rec);
    return {
      rec,
      lat: rec.lat,
      lon: rec.lon,
      headingDeg: rec.headingDeg,
      speedKn: rec.speedKn,
      confidence: confidenceFor(rec, trustTier),
      staleMs: staleWindowMs(rec, presence),
      trustTier,
      presence,
    };
  });
}

/** Project a theater to envelopes once (no motion / no live tick) — static publish
 *  or test snapshot. */
export function theaterEnvelopes(def: TheaterDef): CanonicalEnvelope[] {
  return buildTheaterTracks(def).map((t) => toEnvelope(t, def.id));
}

/** Start a live theater: emit every contact once, then advance (straight-line) +
 *  re-emit on each tick so the picture is alive. Every envelope flows through the
 *  bus → PDP filter → render path. */
export function startTheaterScenario(def: TheaterDef, bus: EnvelopeBus, tickMs = 1_000): TheaterHandle {
  const tracks = buildTheaterTracks(def);
  // Each tick: resolve every track's position PURELY from the wall clock (epoch-anchored,
  // straight-line, period-wrapped), assign it, and re-emit. No dt accumulation — two clients
  // started 60s apart compute the identical picture (deterministic shared COP).
  const emitAll = (): void => {
    const now = Date.now();
    for (const t of tracks) {
      const p = straightLinePositionAtMs(t.rec as MotionRecord, now);
      t.lat = p.lat;
      t.lon = p.lon;
      t.headingDeg = p.headingDeg;
      void bus.publish(toEnvelope(t, def.id));
    }
  };
  emitAll();
  const timer = window.setInterval(emitAll, tickMs);
  return { stop: () => window.clearInterval(timer) };
}
